import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Typography } from '@mui/material'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts'
import { GraphContainer, GraphTitle, GraphExpandButton, GraphWrapper } from '../../CardStyles'
import { GraphProps } from '../../../utils/types'
import { formatYAxisDroppedPackets, prepareGraphData } from '../../../utils/graphs'
import { DOWNLOAD_COLOR, UPLOAD_COLOR, axisStyle } from '../../../utils/constants'
import DroppedPacketsTooltip from './tooltips/DroppedPacketsTooltip'

const DroppedPacketsGraph: React.FC<GraphProps> = ({ data, legendData, isExpanded, onExpand, hideTitle = false }) => {
  const { t, i18n } = useTranslation()
  
  const { filteredDisplayData, xAxisTicks } = useMemo(() => 
    prepareGraphData(data, legendData, isExpanded, ['droppedPacketsDownload', 'droppedPacketsUpload']), 
    [data, legendData, isExpanded, i18n.language]
  );

  if (filteredDisplayData.length === 0) {
    return (
      <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
        {!hideTitle && (
          <GraphTitle>
            <Typography variant="subtitle2">{t('wan.graphs.dropped_packets.title')}</Typography>
            {!isExpanded && (
              <GraphExpandButton onClick={onExpand}>
                <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
              </GraphExpandButton>
            )}
          </GraphTitle>
        )}
        <Box sx={{ 
          height: isExpanded ? '100%' : 100, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center' 
        }}>
          <Typography 
            variant="body2" 
            color="text.secondary"
          >
            {t('wan.graphs.no_data')}
          </Typography>
        </Box>
      </GraphContainer>
    )
  }  

  return (
    <GraphContainer sx={{ height: isExpanded ? 465 : 'auto' }}>
      {!hideTitle && (
        <GraphTitle>
          <Typography variant="subtitle2">{t('wan.graphs.dropped_packets.title')}</Typography>
          {!isExpanded && (
            <GraphExpandButton onClick={onExpand}>
              <i className="fa-regular fa-arrow-up-right-and-arrow-down-left-from-center" />
            </GraphExpandButton>
          )}
        </GraphTitle>
      )}
      <GraphWrapper>
        <ResponsiveContainer width="100%" height={isExpanded ? 400 : 200}>
          <AreaChart
            data={filteredDisplayData}
            margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="simplifiedTime"
              tick={{ ...axisStyle }}
              ticks={xAxisTicks}
            />
            <YAxis
              tickFormatter={formatYAxisDroppedPackets}
              tick={{ ...axisStyle }}
            />
            <RechartsTooltip content={<DroppedPacketsTooltip />} />
            <Area
              type="monotone"
              dataKey="droppedPacketsDownload"
              name={t('wan.graphs.dropped_packets.download')}
              fill="#E4EEFA"
              stroke={DOWNLOAD_COLOR}
              strokeWidth={isExpanded ? 2 : 1}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="droppedPacketsUpload"
              name={t('wan.graphs.dropped_packets.upload')}
              fill="#FEF4E9"
              stroke={UPLOAD_COLOR}
              strokeWidth={isExpanded ? 2 : 1}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: 2, 
          mt: 1, 
          mr: 3,
          fontSize: 10
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 13, 
              height: 13, 
              borderRadius: '50%', 
              backgroundColor: DOWNLOAD_COLOR, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <i className="fa-solid fa-arrow-down" style={{ color: 'white', fontSize: 9 }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: '#666' }}>
              {t('wan.graphs.dropped_packets.download_legend')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ 
              width: 13, 
              height: 13, 
              borderRadius: '50%', 
              backgroundColor: UPLOAD_COLOR, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <i className="fa-solid fa-arrow-up" style={{ color: 'white', fontSize: 9 }} />
            </Box>
            <Typography sx={{ fontSize: 10, color: '#666' }}>
              {t('wan.graphs.dropped_packets.upload_legend')}
            </Typography>
          </Box>
        </Box>
      </GraphWrapper>
    </GraphContainer>
  )
}

export default DroppedPacketsGraph